'use client'

import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import { Moon, Sun } from 'lucide-react'

export function ThemeToggle({ className = '', variant = 'icon' }: { className?: string; variant?: 'icon' | 'segmented' }) {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  if (!mounted) {
    if (variant === 'segmented') {
      return <div className={`h-10 w-[148px] rounded-xl border border-border/70 bg-card/70 ${className}`} aria-hidden="true" />
    }
    return (
      <button className={`p-2 rounded-lg transition-colors ${className}`} aria-label="Toggle theme">
        <Sun className="h-4 w-4 text-muted-foreground" />
      </button>
    )
  }

  const isDark = resolvedTheme === 'dark'

  if (variant === 'segmented') {
    return (
      <div className={`flex items-center rounded-xl border border-border/80 bg-card/75 p-1 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.045] ${className}`} role="group" aria-label="Color theme">
        <button
          type="button"
          onClick={() => setTheme('light')}
          aria-pressed={!isDark}
          className={`flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition ${!isDark ? 'bg-gradient-to-r from-[#2563eb] to-[#06b6d4] text-white shadow-[0_8px_24px_-12px_rgba(37,99,235,.9)]' : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'}`}
        >
          <Sun className="h-3.5 w-3.5" /> Light
        </button>
        <button
          type="button"
          onClick={() => setTheme('dark')}
          aria-pressed={isDark}
          className={`flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition ${isDark ? 'bg-gradient-to-r from-[#2563eb] to-[#06b6d4] text-white shadow-[0_8px_24px_-12px_rgba(37,99,235,.9)]' : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'}`}
        >
          <Moon className="h-3.5 w-3.5" /> Dark
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={`p-2 rounded-lg transition-colors hover:bg-accent/50 ${className}`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      {isDark ? (
        <Sun className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
      ) : (
        <Moon className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
      )}
    </button>
  )
}
